import { useId, useRef, useState } from "react";
import HeadlessTippy from "@tippyjs/react/headless";
import { motion } from "framer-motion";
import { EllipsisVertical } from "lucide-react";
import { cx } from "./utils";

/**
 * Vertical "⋮" row-actions menu. The page decides which actions exist and what
 * they do; this only renders them. The menu is attached to document.body, so
 * the table's scroll container can't clip it.
 *
 * @typedef {Object} RowAction
 * @property {string} label
 * @property {() => void} onSelect
 * @property {import("react").ComponentType<{size?: number, strokeWidth?: number}>} [icon]
 * @property {"default"|"danger"} [tone]  Danger items are red and separated from the rest.
 * @property {boolean} [disabled]
 * @property {boolean} [hidden]
 *
 * @param {{ actions: RowAction[], label?: string }} props
 *   `label` names the trigger for screen readers, e.g. "Actions for Jane Doe".
 */
export default function DataTableRowActions({ actions, label = "Row actions" }) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const menuId = useId();
  const visible = actions.filter((a) => !a.hidden);

  const items = () => Array.from(menuRef.current?.querySelectorAll('[role="menuitem"]:not([disabled])') || []);


  if (visible.length === 0) return null;

  const close = (returnFocus = true) => {
    setOpen(false);
    if (returnFocus) triggerRef.current?.focus();
  };

  const onMenuKeyDown = (event) => {
    event.stopPropagation();
    const list = items();
    const index = list.indexOf(document.activeElement);
    if (event.key === "ArrowDown") {
      event.preventDefault();
      list[(index + 1) % list.length]?.focus();
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      list[(index - 1 + list.length) % list.length]?.focus();
    } else if (event.key === "Home") {
      event.preventDefault();
      list[0]?.focus();
    } else if (event.key === "End") {
      event.preventDefault();
      list[list.length - 1]?.focus();
    } else if (event.key === "Escape") {
      event.preventDefault();
      close();
    } else if (event.key === "Tab") {
      close(false);
    }
  };

  const firstDanger = visible.findIndex((a) => a.tone === "danger");

  return (
    <HeadlessTippy
      visible={open}
      onClickOutside={() => setOpen(false)}
      onMount={() => items()[0]?.focus()}
      interactive
      placement="bottom-end"
      offset={[0, 6]}
      appendTo={() => document.body}
      popperOptions={{ modifiers: [{ name: "flip", options: { fallbackPlacements: ["top-end"] } }] }}
      render={(attrs) =>
        open && (
          <motion.div
            {...attrs}
            initial={{ opacity: 0, y: -4, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.12, ease: "easeOut" }}
            // The menu is portalled; keep its clicks/keys from reaching the row's handlers.
            onClick={(e) => e.stopPropagation()}
            onKeyDown={onMenuKeyDown}
          >
            <div
              ref={menuRef}
              id={menuId}
              role="menu"
              aria-label={label}
              className="min-w-[184px] rounded-xl border border-app-primary bg-app-card p-1 shadow-lg shadow-slate-900/[0.08] dark:shadow-black/40"
            >
              {visible.map((action, i) => {
                const Icon = action.icon;
                const danger = action.tone === "danger";
                return (
                  <div key={action.label}>
                    {danger && i === firstDanger && i > 0 && (
                      <div role="separator" className="my-1 border-t border-app-primary" />
                    )}
                    <button
                      type="button"
                      role="menuitem"
                      disabled={action.disabled}
                      onClick={() => {
                        close(false);
                        action.onSelect();
                      }}
                      className={cx(
                        "flex h-9 w-full items-center gap-2.5 rounded-lg px-3 text-left text-sm transition-colors focus:outline-none disabled:cursor-not-allowed disabled:opacity-50",
                        danger
                          ? "text-red-600 hover:bg-red-50 focus:bg-red-50 dark:text-red-400 dark:hover:bg-red-500/10 dark:focus:bg-red-500/10"
                          : "text-app-secondary hover:bg-app-hover hover:text-app-primary focus:bg-app-hover focus:text-app-primary",
                      )}
                    >
                      {Icon && <Icon size={16} strokeWidth={1.75} aria-hidden="true" className="shrink-0" />}
                      <span className="truncate">{action.label}</span>
                    </button>
                  </div>
                );
              })}
            </div>
          </motion.div>
        )
      }
    >
      <button
        ref={triggerRef}
        type="button"
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((o) => !o);
        }}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown" && !open) {
            e.preventDefault();
            setOpen(true);
          }
        }}
        className={cx(
          "inline-flex h-8 w-8 items-center justify-center rounded-lg transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40",
          open
            ? "bg-app-hover text-app-primary"
            : "text-app-muted hover:bg-app-hover hover:text-app-primary",
        )}
      >
        <EllipsisVertical size={18} strokeWidth={1.75} aria-hidden="true" />
      </button>
    </HeadlessTippy>
  );
}

/**
 * Standard compact actions column so every table places the menu the same way.
 *
 * @template T
 * @param {(row: T) => RowAction[]} getActions
 * @param {{ label?: string, getRowLabel?: (row: T) => string }} [options]
 */
export function createActionsColumn(getActions, { label = "Actions", getRowLabel } = {}) {
  return {
    key: "__actions",
    label,
    align: "right",
    width: "72px",
    searchable: false,
    render: (_, row) => (
      <DataTableRowActions
        actions={getActions(row)}
        label={getRowLabel ? `Actions for ${getRowLabel(row)}` : "Row actions"}
      />
    ),
  };
}
